import { Component, Input } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Location } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { LearnsetService } from 'src/app/core/learnset.service';
import { Learnset } from 'src/app/models/learnset.model';
import { Flashcard } from 'src/app/models/flashcard.model';
import { forkJoin } from 'rxjs';
import { User } from 'src/app/models/user.model';
import { UserService } from 'src/app/core/user.service';
import { FileService } from 'src/app/core/file.service';

@Component({
  selector: 'app-create-upload-flashcards',
  templateUrl: './create-upload-flashcards.component.html',
  styleUrls: ['./create-upload-flashcards.component.scss'],
  standalone: false
})
export class CreateUploadFlashcardsComponent {

  @Input() isDemo: boolean = false;

  breadcrumbs: any[] = [];
  user?: User;
  courseId?: number;
  learnset?: Learnset | null;
  
  uploadStepForm?: FormGroup;
  textStepForm?: FormGroup;
  step: number = 0;
  titles: string[] = ['Lernkarten mit KI erstellen', 'Lernkarten werden erstellt', 'Deine Lernkarten wurden erstellt'];
  nextButtonTexts: string[] = ['Lernkarten erstellen', '', 'Alle übernehmen'];

  isLoading: boolean = false;
  isSaving: boolean = false;
  pdfReady: boolean = false;
  hasError: boolean = false;
  errorMessage: string = '';
  
  pdfSrc: any;
  uploadedFiles?: File[];
  pages?: any[];
  flashcards: Flashcard[] = [];
  flashcardsCount: number = 0;

  storageFileNames?: string[];
  filesUploading: boolean = false;

  subscriptions: any[] = [];

  tabButtons: any = [
    {
      text: 'Aus Datei',
      icon: 'cloud-upload',
      view: 'file-upload'
    },
    {
      text: 'Aus Prompt',
      icon: 'forms',
      view: 'text'
    },
  ];
  selectedTabButton: any = this.tabButtons[0];

  fileProcessingMessages: string[] = [
    'Deine Datei wird analysiert 🔎',
    'Einen Moment Geduld noch ☕',
    'Lernkarten werden erstellt ✨',
    'Gleich ist es so weit 😉',
    'Ein letzter Feinschliff 💎'
  ];
  textProcessingMessages: string[] = [
    'Deine Eingabe wird analysiert 🔎',
    'Einen Moment Geduld noch ☕',
    'Lernkarten werden erstellt ✨',
    'Gleich ist es so weit 😉',
    'Ein letzter Feinschliff 💎'
  ];

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private learnsetService: LearnsetService,
    private userService: UserService,
    private location: Location,
    private fileService: FileService
  ) {}

  ngOnInit() {
    this.userService.getUser().subscribe((user) => this.user = user);
    this.uploadStepForm = this.fb.group({
      promptActive: [false, [Validators.required]],
      prompt: ['', []]
    });
    this.textStepForm = this.fb.group({
      text: ['', [Validators.required]],
      learncardsRange: ['20-30', [Validators.required]]
    });
    
    this.route.queryParams.subscribe(params => {
      const courseId = params['courseId'];
      this.courseId = courseId ? courseId : this.courseId;
      
      const type = params['type'];
      const foundTab = this.tabButtons.find((tab: any) => tab.view === type);
      if (foundTab) {
        this.selectedTabButton = foundTab;
      } else {
        this.changeView(this.tabButtons[0]);
      }
    });

    this.learnsetService.getLearnset(this.route.snapshot.paramMap.get('id')!).subscribe((learnset) => {
      this.learnset = Learnset.constructLearnset(learnset);
      this.breadcrumbs = [
        {
          text: this.learnset.name,
          route: `sets/${this.learnset.id}`
        },
        {
          text: 'Lernkarten erstellen'
        }
      ];
    });
  }

  changeView(event: any) {
    const queryParams = new URLSearchParams(window.location.search);
    queryParams.set('type', event.view);

    const newUrl = window.location.pathname + '?' + queryParams.toString();
    this.location.replaceState(newUrl);
  }

  prevStep() {
    if (this.step === 1 || this.step === 2) {
      this.subscriptions.forEach(subscription => subscription.unsubscribe());
      this.step = 0;
      this.errorMessage = '';
    } else {
      this.step = this.step > 0 ? this.step - 1 : this.step;
    }
  } 

  nextStep() {
    if (this.step === 0) {
      if (this.selectedTabButton.view === 'file-upload') {
        this.uploadStepForm?.markAllAsTouched();
        if (this.uploadedFiles && this.uploadedFiles!.length > 0 && this.uploadStepForm?.valid && !this.filesUploading) {
          this.generateFlashcardsFromFile();
          this.step++;
        }
      } else {
        this.textStepForm?.markAllAsTouched();
        if (this.textStepForm?.valid) {
          this.generateFlashcardsFromText();
          this.step++;
        }
      }
    }
  }

  onFilesChanged(files: any): void {
    this.uploadedFiles = files;

    if (this.uploadedFiles && this.uploadedFiles.length > 0) {
      this.filesUploading = true;

      forkJoin(
        files.map((file: any) => this.fileService.uploadFile(file))
      ).subscribe({
        next: (results: any) => {
          this.storageFileNames = results.map((r: any) => r.fileName);
        },
        complete: () => {
          this.filesUploading = false;
          if (files.length === 1 && files[0].type === 'application/pdf') {
            const reader = new FileReader();

            reader.onload = (e: any) => {
              this.pdfSrc = e.target.result;
            };

            reader.readAsArrayBuffer(files[0]);
            this.pdfReady = true;
          }
        }
      });
    }
  }

  async generateFlashcardsFromFile() {
    const prompt = this.uploadStepForm?.get('promptActive')?.value ? this.uploadStepForm?.get('prompt')?.value : '';

    this.fileService.generateFlashcardsFromFile(this.storageFileNames!, prompt).subscribe(
      (result) => {
        this.pages = result.flashcards;
        this.step++;
        this.calcTotalFlashcards();
      },
      () => {
        this.isLoading = false;
        this.hasError = true;
        this.errorMessage = 'Es gab ein Problem beim Erstellen der Lernkarten. Bitte versuche es nochmal.';
      }
    );
  }

  async generateFlashcardsFromText() {
    const textContent = this.textStepForm?.get('text')?.value;
    const learncardsRange = this.textStepForm?.get('learncardsRange')?.value;
    const subscription = this.learnsetService.generateFromText(textContent, learncardsRange).subscribe(
      (result: any) => {
        this.flashcards = result;
        this.step++;
      },
      () => {
        this.isLoading = false;
        this.hasError = true;
        this.errorMessage = 'Es gab ein Problem beim Erstellen der Lernkarten. Bitte versuche es nochmal.';
      }
    );
    this.subscriptions.push(subscription);
  }

  calcTotalFlashcards() {
    this.flashcardsCount = this.pages!.reduce((acc, page) => {
      return Number(acc) + (page ? page.length : 0);
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }
}
